import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavBarResponse } from './types';
import type { NavigationContextType, NavItem } from './types';

const NavigationContext = createContext<NavigationContextType>({
    navItems: [],
    loading: false,
    error: null,
    hasFetched: false,
    refetch: () => Promise.resolve(),
});

export interface NavigationProviderProps {
    children: React.ReactNode;
    apiEndpoint?: string;
    fetchFn?: (url: string) => Promise<Response>;
    isReady?: boolean;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({
    children,
    apiEndpoint: apiEndpointProp,
    fetchFn: fetchFnProp,
    isReady: isReadyProp,
}) => {
    const [navItems, setNavItems] = useState<NavItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [hasFetched, setHasFetched] = useState(false);

    const apiEndpoint = apiEndpointProp ?? (typeof window !== 'undefined' ? (window as any).REACT_APP_NAVBAR_API_ENDPOINT || '' : '');
    const isReady = isReadyProp ?? true;

    const fetchFnRef = useRef(fetchFnProp);
    fetchFnRef.current = fetchFnProp;

    const fetchNavItems = useCallback(async () => {
        if (!apiEndpoint || !isReady) return;

        setLoading(true);
        setError(null);
        try {
            const fetchFn = fetchFnRef.current ?? (async (url: string) => fetch(url));
            const response = await fetchFn(apiEndpoint.endsWith('/') ? apiEndpoint : `${apiEndpoint}/`);
            const body: NavBarResponse = await response.json();
            if (body.navItems) {
                setNavItems(body.navItems);
            }
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            console.error(e);
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, [apiEndpoint, isReady]);

    useEffect(() => {
        fetchNavItems();
    }, [fetchNavItems]);

    const value: NavigationContextType = {
        navItems,
        loading,
        error,
        hasFetched,
        refetch: fetchNavItems,
    };

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = (): NavigationContextType => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};
